/**
 * OperationalHeatmap v4 — Analytical Decision Panel
 *
 * Features:
 *  - Utilization-based density coloring (not count-based)
 *  - Temporal animation (play/pause timeline)
 *  - Simple load forecast (dashed cells)
 *  - SVG load line overlay
 *  - Automatic bottleneck detection + label
 *  - Trend indicator (↑ ↓ →)
 *  - Professional tooltip (utilization %, status)
 *  - Comparison mode architecture (dataBaseline / dataSimulated)
 */

import React, { useState, useMemo, useRef, useLayoutEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle, Clock, TrendingUp, TrendingDown,
  Minus, Users, X,
} from 'lucide-react';
import {
  getHeatmapMatrix,
  DAY_LABELS,
  type HeatmapFilters,
  type HeatmapCell,
  type DayIndex,
  type HeatmapTaskRow,
  type CapacityConfig,
} from '../services/heatmapService';
import { getOccupancyTier, OCCUPANCY_STATUS_LABEL, OCCUPANCY_TEXT_COLOR } from './ops/executive/opsExecutiveShared';
import {
  enrichCells,
  detectBottlenecks,
  computeForecast,
  computeTrend,
  computeLoadLine,
  getUtilizationColor,
  getHeatmapTextColor,
  type EnrichedCell,
  type ForecastCell,
  type Trend,
} from '../services/heatmapEngine';

// ─── Types ────────────────────────────────────────────────────────────────────

interface OperationalHeatmapProps {
  /** Filtros do ribbon (período, setor, busca) */
  filters: HeatmapFilters;
  capacityConfig?: CapacityConfig;
  /** Multiply displayed cell load by this factor (simulation mode). Does not refetch. */
  simulationMultiplier?: number;
  className?: string;
  /** Preenche o container pai — grade estica na viewport */
  fill?: boolean;
  /** Modo compacto (~20% menor) para aba Carga */
  compact?: boolean;
  /** Maximiza área da grade — KPI inline, células fixas, sem scroll vertical */
  maximizeGrid?: boolean;
  title?: string;
  subtitle?: string;
}

type ViewMode = 'today' | 'workweek' | 'fullweek';

const WORK_DAYS: DayIndex[] = [1, 2, 3, 4, 5];
const FULL_WEEK: DayIndex[] = [0, 1, 2, 3, 4, 5, 6];
const WORK_HOURS: number[] = Array.from({ length: 15 }, (_, i) => i + 6);
const ALL_HOURS: number[]  = Array.from({ length: 24 }, (_, i) => i);
const CELL_H = 32;
/** Altura fixa otimizada — aba Carga (7 dias × 48px cabem em 1080p sem scroll) */
const COMPACT_FILL_CELL_H = 48;
const COMPACT_FILL_ROW_GAP = 2;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtHour(h: number): string {
  return `${h.toString().padStart(2, '0')}h`;
}

function fmtMin(min: number | null): string {
  if (min === null) return '—';
  if (min < 60) return `${Math.round(min)}m`;
  return `${Math.floor(min / 60)}h ${Math.round(min % 60)}m`;
}

function statusLabel(u: number): string {
  if (u === 0) return '—';
  return OCCUPANCY_STATUS_LABEL[getOccupancyTier(u * 100)];
}

function statusColor(u: number): string {
  if (u === 0) return 'var(--text-muted)';
  return OCCUPANCY_TEXT_COLOR[getOccupancyTier(u * 100)];
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function InlineMetric({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div className="flex items-center gap-1">
      <span className="text-[9px] text-[var(--text-muted)]">{label}:</span>
      <span className="text-[10px] font-semibold tabular-nums" style={{ color: color ?? 'var(--text-primary)' }}>
        {value}
      </span>
    </div>
  );
}

function KpiCard({
  label,
  value,
  sub,
  accent,
  inline,
}: {
  label: string;
  value: string;
  sub?: string;
  accent?: 'ok' | 'warn' | 'critical';
  inline?: boolean;
}) {
  const accentBorder =
    accent === 'critical' ? 'border-l-red-500'
    : accent === 'warn' ? 'border-l-amber-500'
    : accent === 'ok' ? 'border-l-emerald-500'
    : 'border-l-transparent';

  if (inline) {
    return (
      <div className={`flex min-w-0 flex-1 items-baseline gap-1.5 border-l-2 px-2 py-0.5 ${accentBorder}`}>
        <span className="shrink-0 text-[8px] font-semibold uppercase tracking-wide text-[var(--text-muted)]">{label}</span>
        <span className="truncate text-[11px] font-bold tabular-nums text-[var(--text-primary)]">{value}</span>
      </div>
    );
  }

  return (
    <div className={`rounded-[4px] border border-[var(--border-default)]/70 bg-[var(--bg-elevated)]/60 px-3 py-2 border-l-2 ${accentBorder}`}>
      <p className="text-[9px] font-semibold uppercase tracking-[0.08em] text-[var(--text-muted)]">{label}</p>
      <p className="mt-0.5 text-[14px] font-bold tabular-nums leading-none text-[var(--text-primary)]">{value}</p>
      {sub && <p className="mt-1 text-[9px] text-[var(--text-secondary)]">{sub}</p>}
    </div>
  );
}

function HeatmapCellContent({
  count,
  utilization,
  showUtilization,
  isForecast,
  isDark,
  dense = false,
}: {
  count: number;
  utilization: number;
  showUtilization: boolean;
  isForecast: boolean;
  isDark: boolean;
  dense?: boolean;
}) {
  const textColor = getHeatmapTextColor(utilization, isDark);

  if (dense) {
    return (
      <div
        className="pointer-events-none flex h-full flex-col items-center justify-center leading-none"
        style={{ color: textColor }}
      >
        <span className="text-[10px] font-bold tabular-nums tracking-tight">
          {isForecast ? '~' : ''}{count}
        </span>
        <span className="text-[6px] font-bold uppercase tracking-wider opacity-75">exec</span>
        {showUtilization && utilization > 0 && (
          <>
            <span className="mt-px text-[9px] font-bold tabular-nums">{Math.round(utilization * 100)}%</span>
            <span className="text-[6px] font-bold uppercase tracking-wider opacity-75">ocup</span>
          </>
        )}
      </div>
    );
  }

  return (
    <div
      className="pointer-events-none flex h-full flex-col items-center justify-center gap-1 px-0.5"
      style={{ color: textColor }}
    >
      <div className="flex flex-col items-center leading-none">
        <span className="text-[12px] font-bold tabular-nums tracking-tight">
          {isForecast ? '~' : ''}{count}
        </span>
        <span className="mt-0.5 text-[7px] font-bold uppercase tracking-[0.14em] opacity-75">
          tarefas
        </span>
      </div>
      {showUtilization && utilization > 0 && (
        <div className="flex flex-col items-center leading-none">
          <span className="text-[10px] font-bold tabular-nums">{Math.round(utilization * 100)}%</span>
          <span className="mt-0.5 text-[7px] font-bold uppercase tracking-[0.14em] opacity-75">
            ocup.
          </span>
        </div>
      )}
    </div>
  );
}

function LegendItem({ color, label, dashed, mini }: { color: string; label: string; dashed?: boolean; mini?: boolean }) {
  return (
    <div className="flex items-center gap-1">
      <div
        className="rounded-[2px]"
        style={{
          width: mini ? 12 : 18,
          height: mini ? 7 : 10,
          backgroundColor: color,
          border: dashed ? '1px dashed var(--border-default)' : '1px solid var(--border-default)',
          opacity: dashed ? 0.6 : 1,
        }}
      />
      <span className={`text-[var(--text-muted)] ${mini ? 'text-[8px]' : 'text-[9px]'}`}>{label}</span>
    </div>
  );
}

// ─── SVG Load Line ────────────────────────────────────────────────────────────

function LoadLineOverlay({
  loadLine, hours, cellWidth, cellHeight = CELL_H,
}: {
  loadLine: { hour: number; utilization: number }[];
  hours: number[];
  cellWidth: number;
  cellHeight?: number;
}) {
  if (loadLine.every(p => p.utilization === 0)) return null;
  const maxUtil = Math.max(...loadLine.map(p => p.utilization), 1);
  const svgH = cellHeight;
  const dayLabelW = 36;
  const points = loadLine.map((p, i) => {
    const x = dayLabelW + i * cellWidth + cellWidth / 2;
    const y = svgH - (p.utilization / maxUtil) * (svgH - 4) - 2;
    return `${x},${y}`;
  });
  return (
    <svg
      className="pointer-events-none absolute inset-0"
      style={{ width: '100%', height: svgH, zIndex: 3 }}
    >
      <polyline
        points={points.join(' ')}
        fill="none"
        stroke="rgba(255,255,255,0.55)"
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
        strokeDasharray="3 2"
      />
      {loadLine.map((p, i) => p.utilization > 0 && (
        <circle
          key={i}
          cx={dayLabelW + i * cellWidth + cellWidth / 2}
          cy={svgH - (p.utilization / maxUtil) * (svgH - 4) - 2}
          r={2}
          fill="rgba(255,255,255,0.7)"
        />
      ))}
    </svg>
  );
}

// ─── Trend badge ──────────────────────────────────────────────────────────────

function TrendBadge({ trend }: { trend: Trend }) {
  if (trend === 'up')   return <span className="flex items-center gap-0.5 text-[9px] font-bold text-red-400"><TrendingUp size={10} /> Alta</span>;
  if (trend === 'down') return <span className="flex items-center gap-0.5 text-[9px] font-bold text-emerald-400"><TrendingDown size={10} /> Queda</span>;
  return <span className="flex items-center gap-0.5 text-[9px] font-medium text-[var(--text-muted)]"><Minus size={10} /> Estável</span>;
}

// ─── Drill-down modal ─────────────────────────────────────────────────────────

function taskDisplayTitle(task: HeatmapTaskRow): string {
  if (task.stage_name?.trim()) return task.stage_name.trim();
  if (task.instance_title?.trim()) return task.instance_title.trim();
  return 'Tarefa de workflow';
}

function DrillDownModal({
  cell, enriched, rows, capacityConfig, scopeLabel, onClose,
}: {
  cell: HeatmapCell;
  enriched: EnrichedCell | undefined;
  rows: HeatmapTaskRow[];
  capacityConfig?: CapacityConfig;
  scopeLabel?: string | null;
  onClose: () => void;
}) {
  const tasks = useMemo(() => rows.filter(r => cell.taskIds.includes(r.id)), [rows, cell.taskIds]);
  const util = enriched?.utilization ?? 0;
  const uniquePeople = useMemo(
    () => new Set(tasks.map((task) => task.assigned_to).filter(Boolean)).size,
    [tasks],
  );
  const dominantDept = useMemo(() => {
    const counts = new Map<string, number>();
    for (const task of tasks) {
      if (!task.department_name) continue;
      counts.set(task.department_name, (counts.get(task.department_name) ?? 0) + 1);
    }
    const top = [...counts.entries()].sort((a, b) => b[1] - a[1])[0];
    return top?.[0] ?? capacityConfig?.departmentName ?? scopeLabel ?? null;
  }, [tasks, capacityConfig?.departmentName, scopeLabel]);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/55 p-4 sm:p-6" onClick={onClose}>
      <div
        className="flex max-h-[min(560px,88vh)] w-full max-w-xl flex-col overflow-hidden rounded-[8px] border border-[var(--border-default)] bg-[var(--bg-surface)] shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-[var(--border-default)] px-4 py-3">
          <div className="min-w-0 flex-1">
            <p className="text-[12px] font-semibold text-[var(--text-primary)]">
              {DAY_LABELS[cell.day]} · {fmtHour(cell.hour)}–{fmtHour(cell.hour + 1)}
            </p>
            <p className="mt-0.5 text-[10px] text-[var(--text-secondary)]">
              {dominantDept ? `Setor: ${dominantDept}` : scopeLabel ? `Escopo: ${scopeLabel}` : 'Escopo: organização'}
              {uniquePeople > 0 && <> · {uniquePeople} pessoa{uniquePeople !== 1 ? 's' : ''} envolvida{uniquePeople !== 1 ? 's' : ''}</>}
            </p>
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              <span className="rounded-[3px] bg-[var(--bg-elevated)] px-2 py-0.5 text-[10px] font-medium text-[var(--text-secondary)]">
                {tasks.length} execuç{tasks.length === 1 ? 'ão' : 'ões'}
              </span>
              {util > 0 && (
                <span className="text-[10px] font-bold" style={{ color: statusColor(util) }}>
                  {statusLabel(util)} · {Math.round(util * 100)}% ocupação
                </span>
              )}
            </div>
          </div>
          <button type="button" onClick={onClose} className="shrink-0 rounded p-1 hover:bg-[var(--button-hover)]">
            <X size={13} className="text-[var(--text-secondary)]" />
          </button>
        </div>

        {util > 0 && (
          <div className="border-b border-[var(--border-default)] px-4 py-2">
            <div className="flex items-center gap-3">
              <div className="flex-1 h-1.5 overflow-hidden rounded-full bg-[var(--bg-app)]">
                <div
                  className="h-full rounded-full transition-all duration-300"
                  style={{ width: `${Math.min(100, Math.round(util * 100))}%`, backgroundColor: statusColor(util) }}
                />
              </div>
              <span className="text-[10px] font-semibold tabular-nums" style={{ color: statusColor(util) }}>
                {Math.round(util * 100)}% ocupação
              </span>
            </div>
            {enriched && (
              <div className="mt-1.5 flex flex-wrap items-center gap-4">
                <InlineMetric label="Tarefas simultâneas" value={String(enriched.count)} />
                {capacityConfig && (
                  <InlineMetric
                    label="Capacidade horária"
                    value={`${capacityConfig.totalCapacityPerHour.toFixed(1)} tarefas/h`}
                  />
                )}
                {enriched.estimatedMinutes > 0 && (
                  <InlineMetric label="Tempo estimado na hora" value={fmtMin(enriched.estimatedMinutes)} />
                )}
                {enriched.avgQueueMinutes != null && (
                  <InlineMetric label="Fila média" value={fmtMin(enriched.avgQueueMinutes)} color={enriched.avgQueueMinutes > 60 ? '#FBBF24' : undefined} />
                )}
              </div>
            )}
          </div>
        )}

        {getOccupancyTier(util * 100) === 'critical' && capacityConfig && (
          <div className="flex shrink-0 items-start gap-2.5 border-b border-[var(--border-default)] bg-red-500/8 px-4 py-2.5">
            <Users size={12} className="mt-0.5 shrink-0 text-red-400" />
            <p className="text-[10px] leading-relaxed text-red-300">
              <strong>Sobrecarga:</strong> {Math.round(util * 100)}% da capacidade de{' '}
              <strong>{capacityConfig.departmentName ?? capacityConfig.topRoleLabel ?? 'equipe'}</strong>
              {' '}({capacityConfig.fte} FTE · {capacityConfig.totalCapacityPerHour.toFixed(1)} tarefas/h).
              Redistribua tarefas ou aumente o time.
            </p>
          </div>
        )}
        {getOccupancyTier(util * 100) === 'high' && (
          <div className="flex items-start gap-2.5 border-b border-[var(--border-default)] bg-amber-500/8 px-4 py-2.5">
            <AlertTriangle size={12} className="mt-0.5 shrink-0 text-amber-400" />
            <p className="text-[10px] leading-relaxed text-amber-400">
              <strong>Atenção:</strong> próximo do limite de capacidade ({Math.round(util * 100)}%).
            </p>
          </div>
        )}

        <div className="min-h-0 flex-1 divide-y divide-[var(--border-default)] overflow-y-auto">
          {tasks.length === 0 ? (
            <p className="py-6 text-center text-[11px] text-[var(--text-muted)]">Nenhuma execução encontrada</p>
          ) : tasks.map(t => (
            <div key={t.id} className="flex items-start gap-3 px-4 py-2.5">
              <div className="min-w-0 flex-1">
                <div className="truncate text-[11px] font-semibold text-[var(--text-primary)]">
                  {taskDisplayTitle(t)}
                </div>
                {t.instance_title && t.stage_name && (
                  <div className="truncate text-[9px] text-[var(--text-muted)]">{t.instance_title}</div>
                )}
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[9px] text-[var(--text-muted)]">
                  <span>Resp: {t.assigned_to_name ?? 'Não atribuído'}</span>
                  {t.department_name && <span>Setor: {t.department_name}</span>}
                  {t.queue_duration != null && <span>Fila: {fmtMin(t.queue_duration)}</span>}
                  {t.planned_duration != null && <span>Previsto: {t.planned_duration} min</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export const OperationalHeatmap: React.FC<OperationalHeatmapProps> = ({
  filters,
  capacityConfig,
  simulationMultiplier,
  className,
  fill = false,
  compact = false,
  maximizeGrid = false,
  title = 'Heatmap de Carga',
  subtitle,
}) => {
  const { isDark } = useTheme();
  const isMaximized = maximizeGrid || (fill && compact);
  const gridAreaRef = useRef<HTMLDivElement>(null);
  const [gridAreaHeight, setGridAreaHeight] = useState(0);
  const [viewMode,     setViewMode]     = useState<ViewMode>(fill ? (compact ? 'fullweek' : 'workweek') : 'today');
  const [hoveredKey,   setHoveredKey]   = useState<string | null>(null);
  const [clickedCell,  setClickedCell]  = useState<HeatmapCell | null>(null);
  const [displayMode,  setDisplayMode]  = useState<'density' | 'stage'>('density');

  const { data, isLoading, isError } = useQuery({
    queryKey: [
      'heatmap-v2',
      filters.organizationId,
      filters.startDate,
      filters.endDate,
      filters.assigneeIds?.join(',') ?? 'all',
      filters.searchText ?? '',
      filters.scopeLabel ?? '',
    ],
    queryFn: () => getHeatmapMatrix(filters),
    enabled: Boolean(filters.organizationId),
    staleTime: 5 * 60 * 1000,
  });

  const todayDayIndex = useMemo(() => new Date().getDay() as DayIndex, []);

  const days = useMemo((): DayIndex[] => {
    if (viewMode === 'today')    return [todayDayIndex];
    if (viewMode === 'workweek') return WORK_DAYS;
    return FULL_WEEK;
  }, [viewMode, todayDayIndex]);

  const hours = viewMode === 'fullweek' ? ALL_HOURS : WORK_HOURS;
  const cellWidth = compact
    ? (viewMode === 'fullweek' ? 24 : 32)
    : (viewMode === 'fullweek' ? 28 : 40);
  const baseCellH = compact ? Math.round(CELL_H * 0.8) : CELL_H;

  useLayoutEffect(() => {
    if (!fill || isMaximized || !gridAreaRef.current) return;
    const el = gridAreaRef.current;
    const ro = new ResizeObserver(([entry]) => {
      setGridAreaHeight(entry.contentRect.height);
    });
    ro.observe(el);
    setGridAreaHeight(el.getBoundingClientRect().height);
    return () => ro.disconnect();
  }, [fill, isMaximized, viewMode, displayMode]);

  const cellHeight = useMemo(() => {
    if (isMaximized) return COMPACT_FILL_CELL_H;
    if (!fill || gridAreaHeight <= 0) return baseCellH;
    const rowCount = Math.max(1, days.length);
    const maxH = compact ? 42 : 52;
    const minH = compact ? 20 : 26;
    const theadReserve = compact ? 22 : 28;
    const rowGaps = Math.max(0, rowCount - 1) * 3;
    const safeMargin = compact ? 10 : 6;
    const usable = gridAreaHeight - theadReserve - rowGaps - safeMargin;
    return Math.max(minH, Math.min(maxH, Math.floor(usable / rowCount)));
  }, [isMaximized, fill, gridAreaHeight, days.length, baseCellH, compact]);

  const rowGap = isMaximized ? COMPACT_FILL_ROW_GAP : 3;

  // ── Enriched cells ─────────────────────────────────────────────────────────

  const simMult = simulationMultiplier ?? 1;

  const enrichedMap = useMemo(
    () => enrichCells(data?.cells ?? [], capacityConfig, simMult),
    [data?.cells, capacityConfig, simMult],
  );

  const primaryDay = days[0];

  const bottlenecks = useMemo(
    () => detectBottlenecks(enrichedMap, hours, primaryDay),
    [enrichedMap, hours, primaryDay],
  );

  const forecasts = useMemo(
    () => computeForecast(enrichedMap, hours, primaryDay),
    [enrichedMap, hours, primaryDay],
  );

  const forecastMap = useMemo(() => {
    const m = new Map<string, ForecastCell>();
    for (const f of forecasts) m.set(`${f.day}-${f.hour}`, f);
    return m;
  }, [forecasts]);

  const trend = useMemo(
    () => computeTrend(enrichedMap, hours, primaryDay),
    [enrichedMap, hours, primaryDay],
  );

  const loadLine = useMemo(
    () => computeLoadLine(enrichedMap, hours, primaryDay),
    [enrichedMap, hours, primaryDay],
  );

  const peakKey = useMemo(() => {
    let maxUtil = 0, key: string | null = null;
    for (const [k, cell] of enrichedMap) {
      if (cell.utilization > maxUtil) { maxUtil = cell.utilization; key = k; }
    }
    return key;
  }, [enrichedMap]);

  const peakInfo = useMemo(() => {
    if (!peakKey) return null;
    const cell = enrichedMap.get(peakKey);
    if (!cell) return null;
    const parts = peakKey.split('-');
    return { day: Number(parts[0]) as DayIndex, hour: Number(parts[1]), utilization: cell.utilization };
  }, [peakKey, enrichedMap]);

  // ── Smart insight ──────────────────────────────────────────────────────────

  const smartInsight = useMemo(() => {
    if (!data) return null;
    if (bottlenecks.length > 0) {
      const worst = [...bottlenecks].sort((a, b) => b.peakUtilization - a.peakUtilization)[0];
      return {
        type: 'overload' as const,
        text: `Sobrecarga ${fmtHour(worst.startHour)}–${fmtHour(worst.endHour)} (${DAY_LABELS[worst.day]}) · ${Math.round(worst.peakUtilization * 100)}% ocupação`,
        recommendation: `Aumentar +1 FTE no pico (${DAY_LABELS[worst.day]} ${fmtHour(worst.startHour)})`,
      };
    }
    if (capacityConfig) {
      return { type: 'stable' as const, text: 'Operação dentro da capacidade no período' };
    }
    if (data.peakSlot) {
      const { day, hour, count, dominantStage } = data.peakSlot;
      const stage = dominantStage ? ` · "${dominantStage}"` : '';
      return { type: 'peak' as const, text: `Pico: ${DAY_LABELS[day]} ${fmtHour(hour)}–${fmtHour(hour + 1)}${stage} · ${count} tarefa${count !== 1 ? 's' : ''} simultâneas` };
    }
    return data.insight ? { type: 'info' as const, text: data.insight } : null;
  }, [data, bottlenecks, capacityConfig]);

  const hoveredEnriched = hoveredKey ? enrichedMap.get(hoveredKey) : undefined;
  const isEmpty         = !data || data.totalTasks === 0;
  const isGapMode       = !!capacityConfig;

  // ── Stage matrix (for displayMode === 'stage') ──────────────────────────

  const stageMatrix = useMemo(() => {
    if (!data?.rows || data.rows.length === 0) return null;
    const stageMap = new Map<string, Map<number, number>>(); // stage → hour → count
    for (const row of data.rows) {
      if (!row.started_at) continue;
      const stageName = row.stage_name ?? '(sem etapa)';
      const startTs = new Date(row.started_at);
      const endTs = row.completed_at
        ? new Date(row.completed_at)
        : new Date(startTs.getTime() + 60 * 60 * 1000);
      const cursor = new Date(startTs);
      cursor.setMinutes(0, 0, 0);
      let steps = 0;
      while (cursor <= endTs && steps < 48) {
        steps++;
        const h = cursor.getHours();
        if (!stageMap.has(stageName)) stageMap.set(stageName, new Map());
        const hm = stageMap.get(stageName)!;
        hm.set(h, (hm.get(h) ?? 0) + 1);
        cursor.setHours(cursor.getHours() + 1);
      }
    }
    if (stageMap.size === 0) return null;
    const stageNames = [...stageMap.keys()].sort();
    const maxCount = Math.max(1, ...stageNames.flatMap(s => [...stageMap.get(s)!.values()]));
    return { stageNames, stageMap, maxCount };
  }, [data?.rows]);

  return (
    <div
      className={`flex flex-col overflow-hidden rounded-[5px] border border-[var(--border-default)] bg-[var(--bg-surface)] ${
        fill ? 'h-full min-h-0 flex-1' : ''
      } ${className ?? ''}`}
      style={fill ? undefined : { minHeight: 260 }}
    >
      {/* ── Header ──────────────────────────────────────────── */}
      <div className={`flex shrink-0 items-center justify-between gap-2 border-b border-[var(--border-default)] ${
        isMaximized ? 'px-3 py-1' : fill ? 'px-4 py-2.5' : 'px-5 py-3'
      }`}>
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <TrendingUp size={isMaximized ? 11 : 12} strokeWidth={2} className="shrink-0 text-[var(--text-secondary)]" />
          <span className={`font-semibold uppercase tracking-[0.1em] text-[var(--text-primary)] ${isMaximized ? 'text-[10px]' : 'text-[11px]'}`}>
            {title}
          </span>
          {isMaximized && subtitle && (
            <span className="truncate text-[9px] text-[var(--text-muted)]">· {subtitle}</span>
          )}
          {isGapMode && !isMaximized && (
            <span className="inline-flex items-center rounded-[3px] bg-[#2F6FED]/18 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wide text-[#60A5FA]">
              vs. capacidade
            </span>
          )}
          {displayMode === 'stage' && !isMaximized && (
            <span className="inline-flex items-center rounded-[3px] bg-violet-500/18 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wide text-violet-400">
              Por etapa
            </span>
          )}
          {!isEmpty && !isMaximized && <TrendBadge trend={trend} />}
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          {!isEmpty && !isMaximized && (
            <button
              type="button"
              onClick={() => setDisplayMode(v => v === 'stage' ? 'density' : 'stage')}
              title="Alternar visualização por etapa do processo"
              className={`rounded-[3px] border px-2 py-1 text-[9px] font-semibold uppercase tracking-wide transition-colors ${
                displayMode === 'stage'
                  ? 'border-violet-500/60 bg-violet-500/15 text-violet-400'
                  : 'border-[var(--border-default)] text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
              }`}
            >
              Por etapa
            </button>
          )}
          <div className="inline-flex overflow-hidden rounded-[3px] border border-[var(--border-default)]">
            {([['today', 'Hoje'], ['workweek', 'Seg–Sex'], ['fullweek', 'Semana']] as [ViewMode, string][]).map(([mode, label]) => (
              <button
                key={mode}
                type="button"
                onClick={() => setViewMode(mode)}
                className={`font-semibold uppercase tracking-wide transition-colors duration-100 ${
                  isMaximized ? 'px-2 py-0.5 text-[9px]' : 'px-3 py-1 text-[10px]'
                } ${
                  viewMode === mode ? 'bg-[#2F6FED] text-white' : 'bg-[var(--bg-surface)] text-[var(--text-secondary)] hover:bg-[var(--button-hover)]'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {!isMaximized && (
        <p className="shrink-0 border-b border-[var(--border-default)]/50 px-4 py-1 text-[10px] leading-relaxed text-[var(--text-secondary)]">
          {subtitle ? `${subtitle} · ` : ''}
          <strong className="font-semibold text-[var(--text-primary)]">
            {data?.scopeLabel ?? filters.scopeLabel ?? 'Toda a organização'}
          </strong>
          {data && <> · {data.uniquePeopleInvolved} pessoa{data.uniquePeopleInvolved !== 1 ? 's' : ''} com tarefas</>}
          {' · '}
          <strong className="font-semibold text-[var(--text-primary)]">tarefas</strong> = simultâneas;
          {isGapMode ? (
            <> <strong className="font-semibold text-[var(--text-primary)]">ocup.</strong> = % da capacidade ({capacityConfig?.fte ?? '—'} FTE).</>
          ) : (
            <> cor = intensidade relativa ao pico.</>
          )}
          {filters.searchText && (
            <> · Busca: <em>{filters.searchText}</em></>
          )}
        </p>
      )}

      {/* ── KPI strip ───────────────────────────────────────── */}
      {!isLoading && !isEmpty && data && (
        <div className={`shrink-0 border-b border-[var(--border-default)]/70 bg-[var(--bg-app)]/40 ${
          isMaximized
            ? 'flex items-stretch divide-x divide-[var(--border-default)]/60'
            : `grid grid-cols-2 gap-2 lg:grid-cols-4 ${fill ? 'px-4 py-2' : 'px-5 py-2.5'}`
        }`}>
          <KpiCard
            label="Tarefas"
            value={String(data.totalTasks)}
            sub={isMaximized ? undefined : 'tarefas de workflow iniciadas'}
            inline={isMaximized}
          />
          <KpiCard
            label="Pico"
            value={peakInfo ? `${DAY_LABELS[peakInfo.day]} ${fmtHour(peakInfo.hour)}` : '—'}
            sub={isMaximized ? undefined : peakInfo ? `${peakInfo.utilization > 0 && isGapMode ? `${Math.round(peakInfo.utilization * 100)}% ocupação · ` : ''}${enrichedMap.get(`${peakInfo.day}-${peakInfo.hour}`)?.count ?? 0} tarefas simultâneas` : undefined}
            accent={capacityConfig && peakInfo && peakInfo.utilization > 1 ? 'critical' : capacityConfig && peakInfo && peakInfo.utilization >= 0.8 ? 'warn' : undefined}
            inline={isMaximized}
          />
          <KpiCard
            label="Capacidade/h"
            value={capacityConfig ? `${capacityConfig.totalCapacityPerHour.toFixed(1)} tarefas/h` : 'Relativa'}
            sub={isMaximized ? undefined : capacityConfig
              ? `${capacityConfig.departmentName ?? capacityConfig.topRoleLabel ?? 'Equipe'} · ${capacityConfig.fte} FTE`
              : 'Configure FTE no ribbon'}
            inline={isMaximized}
          />
          <KpiCard
            label="Situação"
            value={bottlenecks.length > 0 ? `${bottlenecks.length} gargalo${bottlenecks.length !== 1 ? 's' : ''}` : 'Estável'}
            sub={isMaximized ? undefined : smartInsight?.text}
            accent={bottlenecks.length > 0 ? 'critical' : smartInsight?.type === 'stable' ? 'ok' : smartInsight?.type === 'peak' ? 'warn' : undefined}
            inline={isMaximized}
          />
        </div>
      )}

      {/* ── Loading ─────────────────────────────────────────── */}
      {isLoading && (
        <div className="flex flex-1 items-center justify-center py-12">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-[var(--border-default)] border-t-[#2F6FED]" />
        </div>
      )}

      {/* ── Error ───────────────────────────────────────────── */}
      {isError && (
        <div className="flex flex-1 items-center justify-center gap-2 py-8 text-[11px] text-red-400">
          <AlertTriangle size={13} /> Erro ao carregar heatmap
        </div>
      )}

      {/* ── Grid ────────────────────────────────────────────── */}
      {!isLoading && !isError && (
        <div
          ref={gridAreaRef}
          className={`flex min-h-0 flex-1 flex-col overflow-x-auto ${
            isMaximized
              ? 'justify-start overflow-y-hidden px-2 py-1'
              : fill
                ? compact
                  ? 'justify-center px-3 py-5'
                  : 'justify-center px-3 py-4'
                : 'overflow-y-hidden px-5 pb-4 pt-3'
          }`}
        >
          {isEmpty ? (
            <div className="flex h-32 flex-col items-center justify-center gap-1.5 px-6 text-center">
              <span className="text-[12px] font-medium text-[var(--text-secondary)]">Sem volume no período</span>
              <span className="text-[10px] text-[var(--text-muted)]">
                {filters.assigneeIds?.length === 0
                  ? `O setor "${filters.scopeLabel ?? 'selecionado'}" não possui membros vinculados.`
                  : filters.searchText
                    ? `Nenhuma execução encontrada para "${filters.searchText}" no escopo atual.`
                    : `Nenhuma execução iniciada para ${filters.scopeLabel ?? 'a organização'} no intervalo selecionado.`}
              </span>
            </div>
          ) : displayMode === 'stage' && stageMatrix ? (
            // ── Stage × Hour grid ──────────────────────────────
            <table className="border-collapse" style={{ tableLayout: 'fixed', width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ width: 100 }} />
                  {hours.map(h => (
                    <th
                      key={h}
                      className="pb-1.5 pt-0 text-center font-mono text-[8px] font-semibold text-[var(--text-secondary)]"
                      style={{ width: cellWidth }}
                    >
                      {h % 2 === 0 ? fmtHour(h) : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {stageMatrix.stageNames.map(stageName => (
                  <tr key={stageName}>
                    <td
                      className="pr-2.5 text-right text-[9px] font-semibold text-[var(--text-primary)] truncate"
                      style={{ lineHeight: `${cellHeight}px`, maxWidth: 100 }}
                      title={stageName}
                    >
                      {stageName.length > 14 ? stageName.slice(0, 13) + '…' : stageName}
                    </td>
                    {hours.map(h => {
                      const count = stageMatrix.stageMap.get(stageName)?.get(h) ?? 0;
                      const key   = `stage-${stageName}-${h}`;
                      const isHov = key === hoveredKey;
                      const bg    = 'var(--bg-elevated)';
                      return (
                        <td
                          key={h}
                          onMouseEnter={() => setHoveredKey(key)}
                          onMouseLeave={() => setHoveredKey(null)}
                          style={{
                            height: cellHeight,
                            position: 'relative',
                            backgroundColor: bg,
                            border: '1px solid var(--border-default)',
                            boxShadow: isHov && count > 0 ? '0 4px 12px rgba(0,0,0,0.35)' : 'none',
                            transform: isHov && count > 0 ? 'scale(1.06)' : 'scale(1)',
                            filter:    isHov && count > 0 ? 'brightness(1.12)' : 'brightness(1)',
                            zIndex: isHov ? 2 : 1,
                            transition: 'all 0.12s ease',
                          }}
                        >
                          {count > 0 && (
                            <div style={{
                              position: 'absolute', inset: 0,
                              display: 'flex', flexDirection: 'column',
                              alignItems: 'center', justifyContent: 'center',
                              color: 'var(--text-primary)',
                              pointerEvents: 'none',
                            }}>
                              <span style={{ fontSize: count >= 10 ? 8 : 11, fontWeight: 700, fontFamily: 'ui-monospace, monospace', letterSpacing: '-0.02em', lineHeight: 1 }}>
                                {count}
                              </span>
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : displayMode === 'stage' ? (
            // Fallback: no stage data
            <div className="flex h-32 flex-col items-center justify-center gap-1.5">
              <span className="text-[12px] font-medium text-[var(--text-secondary)]">Sem dados de etapa</span>
              <span className="text-[10px] text-[var(--text-muted)]">As tarefas do período não possuem etapa registrada</span>
            </div>
          ) : (
            <div className="w-full">
            <table className="w-full border-separate" style={{ tableLayout: 'fixed', borderSpacing: `${rowGap}px` }}>
              <thead>
                <tr>
                  <th style={{ width: isMaximized ? 32 : 40 }} />
                  {hours.map(h => (
                    <th
                      key={h}
                      className={`pb-0 pt-0 text-center font-mono font-semibold uppercase tracking-wide text-[var(--text-muted)] ${
                        isMaximized ? 'text-[8px]' : 'pb-1 text-[9px]'
                      }`}
                      style={{ width: cellWidth }}
                    >
                      {fmtHour(h)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {days.map(dayIdx => {
                  const showLineForRow = viewMode === 'today' && dayIdx === primaryDay;
                  return (
                    <tr key={dayIdx}>
                      <td
                        className={`pr-1 text-right font-bold uppercase tracking-wide text-[var(--text-secondary)] ${
                          isMaximized ? 'text-[9px]' : 'text-[10px]'
                        }`}
                        style={{
                          lineHeight: isMaximized ? 1.2 : `${cellHeight}px`,
                          verticalAlign: 'middle',
                          height: cellHeight,
                        }}
                      >
                        {DAY_LABELS[dayIdx]}
                      </td>
                      {hours.map((h, hIdx) => {
                        const key        = `${dayIdx}-${h}`;
                        const enriched   = enrichedMap.get(key);
                        const forecast   = forecastMap.get(key);
                        const util       = enriched?.utilization ?? forecast?.utilization ?? 0;
                        const count      = enriched?.count ?? forecast?.count ?? 0;
                        const isFcast    = !enriched && !!forecast;
                        const isPeakCell = key === peakKey && !isFcast;
                        const isHover    = key === hoveredKey;
                        const hasData    = count > 0;
                        const hasCapacity = Boolean(capacityConfig && capacityConfig.totalCapacityPerHour > 0);
                        const isOverload = hasCapacity && util > 1.0 && hasData && !isFcast;
                        const bg = hasData && hasCapacity
                          ? getUtilizationColor(isFcast ? forecast!.utilization : util, isDark)
                          : 'var(--bg-elevated)';

                        return (
                          <td
                            key={h}
                            onMouseEnter={() => setHoveredKey(key)}
                            onMouseLeave={() => setHoveredKey(null)}
                            onClick={() => enriched && hasData ? setClickedCell(enriched) : undefined}
                            style={{
                              height: cellHeight,
                              position: 'relative',
                              backgroundColor: bg,
                              opacity: isFcast ? 0.55 : hasData ? 1 : 0.65,
                              borderRadius: isMaximized ? 3 : 4,
                              border: isOverload
                                ? '1.5px solid #DC2626'
                                : isFcast
                                ? '1.5px dashed rgba(148,163,184,0.55)'
                                : hasData
                                ? '1px solid rgba(15,23,42,0.08)'
                                : '1px dashed var(--border-default)',
                              outline: isPeakCell && hasData ? '2px solid #F59E0B' : 'none',
                              outlineOffset: '-1px',
                              boxShadow: isOverload
                                ? '0 0 0 1px rgba(220,38,38,0.35), 0 2px 8px rgba(239,68,68,0.15)'
                                : isHover && hasData
                                ? '0 4px 14px rgba(15,23,42,0.18)'
                                : 'none',
                              transform:  isHover && hasData && !isFcast && !isMaximized ? 'scale(1.04)' : 'scale(1)',
                              filter:     isHover && hasData ? 'brightness(1.05)' : 'brightness(1)',
                              zIndex:     isHover ? 2 : 1,
                              transition: 'all 0.12s ease',
                              cursor:     hasData && !isFcast ? 'pointer' : 'default',
                            }}
                          >
                            {showLineForRow && hIdx === 0 && (
                              <LoadLineOverlay loadLine={loadLine} hours={hours} cellWidth={cellWidth} cellHeight={cellHeight} />
                            )}
                            {hasData && (
                              <HeatmapCellContent
                                count={count}
                                utilization={util}
                                showUtilization={!!capacityConfig}
                                isForecast={isFcast}
                                isDark={isDark}
                                dense={isMaximized}
                              />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
        </div>
      )}

      {/* ── Footer: tooltip + legenda ───────────────────────── */}
      {isMaximized && !isLoading && !isError && !isEmpty ? (
        <div className="flex shrink-0 items-center justify-between gap-2 border-t border-[var(--border-default)] bg-[var(--bg-elevated)]/80 px-3 py-1">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-0">
            {hoveredEnriched && hoveredEnriched.count > 0 ? (
              <>
                <span className="text-[10px] font-semibold text-[var(--text-primary)]">
                  {DAY_LABELS[hoveredEnriched.day]} {fmtHour(hoveredEnriched.hour)}–{fmtHour(hoveredEnriched.hour + 1)}
                </span>
                <InlineMetric label="Tarefas" value={String(hoveredEnriched.count)} />
                {capacityConfig && (
                  <InlineMetric label="Ocup" value={`${Math.round(hoveredEnriched.utilization * 100)}%`} color={statusColor(hoveredEnriched.utilization)} />
                )}
                <InlineMetric
                  label="Status"
                  value={capacityConfig ? statusLabel(hoveredEnriched.utilization) : '—'}
                  color={capacityConfig ? statusColor(hoveredEnriched.utilization) : undefined}
                />
              </>
            ) : (
              <span className="text-[9px] text-[var(--text-muted)]">Passe o mouse sobre uma célula · tarefas simultâneas · ocupação = % da capacidade</span>
            )}
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <LegendItem mini color={getUtilizationColor(0.25, isDark)} label="Folga" />
            <LegendItem mini color={getUtilizationColor(0.65, isDark)} label="Normal" />
            <LegendItem mini color={getUtilizationColor(0.90, isDark)} label="Atenção" />
            <LegendItem mini color={getUtilizationColor(1.2, isDark)} label="Sobrecarga" />
          </div>
        </div>
      ) : (
        <>
      <div className={`shrink-0 border-t border-[var(--border-default)] bg-[var(--bg-elevated)]/80 ${fill ? 'px-4 py-1.5' : 'px-5 py-2'}`}>
        {hoveredEnriched && hoveredEnriched.count > 0 ? (
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
            <div className="flex items-center gap-1.5">
              <Clock size={10} className="text-[var(--text-muted)]" />
              <span className="text-[11px] font-semibold text-[var(--text-primary)]">
                {DAY_LABELS[hoveredEnriched.day]} · {fmtHour(hoveredEnriched.hour)}–{fmtHour(hoveredEnriched.hour + 1)}
              </span>
            </div>
            <InlineMetric label="Tarefas simultâneas" value={String(hoveredEnriched.count)} />
            {capacityConfig && (
              <InlineMetric
                label="Ocupação da capacidade"
                value={`${Math.round(hoveredEnriched.utilization * 100)}%`}
                color={statusColor(hoveredEnriched.utilization)}
              />
            )}
            <InlineMetric
              label="Status"
              value={statusLabel(hoveredEnriched.utilization)}
              color={statusColor(hoveredEnriched.utilization)}
            />
            {capacityConfig && (
              <InlineMetric
                label="Capacidade"
                value={`${capacityConfig.totalCapacityPerHour.toFixed(1)} tarefas/h (${capacityConfig.fte} FTE)`}
              />
            )}
            {hoveredEnriched.estimatedMinutes > 0 && (
              <InlineMetric label="Tempo estimado" value={fmtMin(hoveredEnriched.estimatedMinutes)} />
            )}
            {hoveredEnriched.dominantStage && <InlineMetric label="Etapa principal" value={hoveredEnriched.dominantStage} />}
            {capacityConfig?.departmentName && (
              <InlineMetric label="Setor" value={capacityConfig.departmentName} />
            )}
            {data && data.uniquePeopleInvolved > 0 && (
              <InlineMetric label="Pessoas no escopo" value={String(data.uniquePeopleInvolved)} />
            )}
            <span className="text-[9px] italic text-[var(--text-muted)]">Clique na célula para ver as tarefas</span>
          </div>
        ) : (
          <p className="text-[10px] text-[var(--text-muted)]">
            Passe o mouse sobre uma célula colorida para ver tarefas simultâneas, ocupação e capacidade da hora.
          </p>
        )}
      </div>

      {!isLoading && !isError && !isEmpty && (
        <div className={`flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-[var(--border-default)] ${fill ? 'px-4 py-1.5' : 'px-5 py-2.5'}`}>
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-[var(--text-muted)]">
              Legenda de ocupação
            </span>
            <LegendItem color={getUtilizationColor(0.25, isDark)} label="Folga (0–50%)" />
            <LegendItem color={getUtilizationColor(0.65, isDark)} label="Normal (>50–80%)" />
            <LegendItem color={getUtilizationColor(0.90, isDark)} label="Atenção (>80–100%)" />
            <LegendItem color={getUtilizationColor(1.2, isDark)} label="Sobrecarga (>100%)" />
            {forecasts.length > 0 && <LegendItem color={getUtilizationColor(0.65, isDark)} label="Previsão" dashed />}
          </div>
          <p className="text-[9px] text-[var(--text-muted)]">
            Célula: <span className="font-semibold text-[var(--text-secondary)]">tarefas</span> = tarefas ativas na hora
            {capacityConfig && (
              <> · <span className="font-semibold text-[var(--text-secondary)]">ocupação</span> = carga observada ÷ capacidade configurada ({capacityConfig.totalCapacityPerHour.toFixed(1)} tarefas/h
                {capacityConfig.fte != null ? ` · FTE ${capacityConfig.fte.toFixed(1)}` : ''}
                {capacityConfig.tasksPerPersonHour != null ? ` · ${capacityConfig.tasksPerPersonHour.toLocaleString('pt-BR')} tarefa/pessoa/h` : ''}
                )</>
            )}
          </p>
        </div>
      )}
        </>
      )}

      {/* ── Drill-down modal ─────────────────────────────────── */}
      {clickedCell && data && (
        <DrillDownModal
          cell={clickedCell}
          enriched={enrichedMap.get(`${clickedCell.day}-${clickedCell.hour}`)}
          rows={data.rows}
          capacityConfig={capacityConfig}
          scopeLabel={data.scopeLabel ?? filters.scopeLabel}
          onClose={() => setClickedCell(null)}
        />
      )}
    </div>
  );
};

export default OperationalHeatmap;

